import * as THREE from "three";

const imageCache = new Map<string, Promise<HTMLImageElement>>();

export function loadSkinImage(url: string): Promise<HTMLImageElement> {
  let pending = imageCache.get(url);
  if (!pending) {
    pending = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`failed to load skin: ${url}`));
      img.src = url;
    });
    imageCache.set(url, pending);
  }
  return pending;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function cropTexture(img: HTMLImageElement, r: Rect, rotate = 0, mirror = false): THREE.Texture {
  const swap = rotate === 90 || rotate === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? r.h : r.w;
  canvas.height = swap ? r.w : r.h;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  if (rotate) ctx.rotate((rotate * Math.PI) / 180);
  if (mirror) ctx.scale(-1, 1);
  ctx.drawImage(img, r.x, r.y, r.w, r.h, -r.w / 2, -r.h / 2, r.w, r.h);

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function faceMaterial(tex: THREE.Texture, overlay: boolean): THREE.Material {
  return new THREE.MeshBasicMaterial({
    map: tex,
    transparent: overlay,
    alphaTest: overlay ? 0.5 : 0,
    side: overlay ? THREE.DoubleSide : THREE.FrontSide,
  });
}

interface BoxSpec {
  w: number;
  h: number;
  d: number;
  u: number;
  v: number;
  overlay?: boolean;
  mirror?: boolean;
}

// three.js BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z.
function buildBox(img: HTMLImageElement, spec: BoxSpec): THREE.Mesh {
  const { w, h, d, u, v, overlay = false, mirror = false } = spec;
  const right: Rect = { x: u, y: v + d, w: d, h };
  const front: Rect = { x: u + d, y: v + d, w, h };
  const left: Rect = { x: u + d + w, y: v + d, w: d, h };
  const back: Rect = { x: u + d + w + d, y: v + d, w, h };
  const top: Rect = { x: u + d, y: v, w, h: d };
  const bottom: Rect = { x: u + d + w, y: v, w, h: d };

  const mats = [
    faceMaterial(cropTexture(img, mirror ? right : left, 0, mirror), overlay),
    faceMaterial(cropTexture(img, mirror ? left : right, 0, mirror), overlay),
    faceMaterial(cropTexture(img, top, 0, mirror), overlay),
    faceMaterial(cropTexture(img, bottom, 180, mirror), overlay),
    faceMaterial(cropTexture(img, front, 0, mirror), overlay),
    faceMaterial(cropTexture(img, back, 0, mirror), overlay),
  ];

  const expand = overlay ? 0.5 : 0;
  const geo = new THREE.BoxGeometry(w + expand, h + expand, d + expand);
  return new THREE.Mesh(geo, mats);
}

export function buildHeadGroup(img: HTMLImageElement): THREE.Group {
  const group = new THREE.Group();
  group.add(buildBox(img, { w: 8, h: 8, d: 8, u: 0, v: 0 }));
  group.add(buildBox(img, { w: 8, h: 8, d: 8, u: 32, v: 0, overlay: true }));
  return group;
}

interface PartPlacement {
  spec: BoxSpec;
  overlayU: number;
  overlayV: number;
  pos: [number, number, number];
}

export function buildPlayerGroup(img: HTMLImageElement, slim: boolean): THREE.Group {
  const legacy = img.height < 64;
  const armW = slim ? 3 : 4;
  const armX = 4 + armW / 2;

  const parts: PartPlacement[] = [
    { spec: { w: 8, h: 8, d: 8, u: 0, v: 0 }, overlayU: 32, overlayV: 0, pos: [0, 10, 0] },
    { spec: { w: 8, h: 12, d: 4, u: 16, v: 16 }, overlayU: 16, overlayV: 32, pos: [0, 0, 0] },
    { spec: { w: armW, h: 12, d: 4, u: 40, v: 16 }, overlayU: 40, overlayV: 32, pos: [-armX, 0, 0] },
    {
      spec: legacy
        ? { w: armW, h: 12, d: 4, u: 40, v: 16, mirror: true }
        : { w: armW, h: 12, d: 4, u: 32, v: 48 },
      overlayU: 48,
      overlayV: 48,
      pos: [armX, 0, 0],
    },
    { spec: { w: 4, h: 12, d: 4, u: 0, v: 16 }, overlayU: 0, overlayV: 32, pos: [-2, -12, 0] },
    {
      spec: legacy
        ? { w: 4, h: 12, d: 4, u: 0, v: 16, mirror: true }
        : { w: 4, h: 12, d: 4, u: 16, v: 48 },
      overlayU: 0,
      overlayV: 48,
      pos: [2, -12, 0],
    },
  ];

  const group = new THREE.Group();
  for (const part of parts) {
    const base = buildBox(img, part.spec);
    base.position.set(...part.pos);
    group.add(base);

    if (!legacy || (part.overlayU === 32 && part.overlayV === 0)) {
      const overlay = buildBox(img, {
        ...part.spec,
        u: part.overlayU,
        v: part.overlayV,
        overlay: true,
      });
      overlay.position.set(...part.pos);
      group.add(overlay);
    }
  }
  return group;
}
